import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Clock, LogIn, LogOut as LogOutIcon, Coffee, Sun, Pencil } from "lucide-react";
import { toast } from "sonner";

const PUNCH_LABELS = {
  clock_in: { label: "Clock In", icon: LogIn, color: "bg-[#10B981] hover:bg-emerald-600" },
  lunch_out: { label: "Lunch Out", icon: Coffee, color: "bg-amber-500 hover:bg-amber-600" },
  lunch_in: { label: "Back from Lunch", icon: Sun, color: "bg-amber-500 hover:bg-amber-600" },
  clock_out: { label: "Clock Out", icon: LogOutIcon, color: "bg-[#002FA7] hover:bg-[#002075]" },
};

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatIsoLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

// Pay week is Thursday → Wednesday. Returns the Thursday date string that starts the week.
function payWeekStart(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay();
  const back = (dow - 4 + 7) % 7;
  dt.setDate(dt.getDate() - back);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function formatRange(start) {
  const end = addDays(start, 6);
  const fmt = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], { month: "short", day: "numeric" });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

import { useAuth } from "../context/AuthContext";

export default function MyTimecard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Own entries vs entries visible via grants (visible_user_ids)
  const ownHistory = history.filter((t) => t.user_id === user?.id);
  const teamHistory = history.filter((t) => t.user_id !== user?.id);

  const load = async () => {
    const [t, h] = await Promise.all([
      api.get("/timecards/today"),
      api.get("/timecards"),
    ]);
    setToday(t.data);
    setHistory(h.data);
  };

  useEffect(() => { load(); }, []);

  const punch = async (action) => {
    try {
      await api.post("/timecards/punch", { action });
      toast.success(PUNCH_LABELS[action].label + " recorded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openEdit = (tc) => {
    setEditing(tc);
    setEditForm({
      clock_in: formatIsoLocal(tc.clock_in),
      lunch_out: formatIsoLocal(tc.lunch_out),
      lunch_in: formatIsoLocal(tc.lunch_in),
      clock_out: formatIsoLocal(tc.clock_out),
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        clock_in: localToIso(editForm.clock_in),
        lunch_out: localToIso(editForm.lunch_out),
        lunch_in: localToIso(editForm.lunch_in),
        clock_out: localToIso(editForm.clock_out),
      };
      await api.patch(`/timecards/${editing.id}`, payload);
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const nextAction = (() => {
    if (!today) return null;
    if (!today.clock_in) return "clock_in";
    if (!today.lunch_out) return "lunch_out";
    if (!today.lunch_in) return "lunch_in";
    if (!today.clock_out) return "clock_out";
    return null;
  })();

  // Group own history into pay weeks (Thursday → Wednesday) with weekly totals
  const myPayrollWeeks = (() => {
    const weeks = new Map();
    for (const t of ownHistory) {
      if (!t.date) continue;
      const ws = payWeekStart(t.date);
      if (!weeks.has(ws)) weeks.set(ws, []);
      weeks.get(ws).push(t);
    }
    return Array.from(weeks.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([weekStart, entries]) => {
        const sorted = entries.slice().sort((a, b) => a.date.localeCompare(b.date));
        return {
          weekStart,
          entries: sorted,
          total: sorted.reduce((s, t) => s + Number(t.total_hours || 0), 0),
        };
      });
  })();

  const totalOwnHours = ownHistory.reduce((s, t) => s + Number(t.total_hours || 0), 0);

  // Split history: own entries stay in the top table.
  // Team entries (from visible_user_ids grants) roll up per-employee per pay-week.
  const teamPayrollWeeks = (() => {
    const weeks = new Map();
    for (const t of teamHistory) {
      if (!t.date) continue;
      const ws = payWeekStart(t.date);
      if (!weeks.has(ws)) weeks.set(ws, new Map());
      const byUser = weeks.get(ws);
      const key = t.user_id;
      if (!byUser.has(key)) {
        byUser.set(key, { name: t.user_name, phone: t.user_phone, hours: 0, entries: [] });
      }
      const row = byUser.get(key);
      row.hours += Number(t.total_hours || 0);
      row.entries.push(t);
    }
    return Array.from(weeks.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([weekStart, byUser]) => {
        const rows = Array.from(byUser.values())
          .map((r) => ({
            ...r,
            entries: r.entries.slice().sort((a, b) => a.date.localeCompare(b.date)),
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return {
          weekStart,
          rows,
          total: rows.reduce((s, r) => s + r.hours, 0),
        };
      });
  })();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-5xl">
      <PageHeader
        eyebrow="Today"
        title="My Timecard"
        description="Clock in, take lunch, clock out. Your hours are tracked automatically."
      />

      {today && (
        <div className="mt-8 bg-white border border-zinc-200 rounded-sm p-8" data-testid="today-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">
                {new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
              </div>
              <div className="font-heading text-5xl tracking-tighter font-black text-zinc-950 mt-2 font-mono" data-testid="today-hours">
                {today.total_hours.toFixed(2)}<span className="text-2xl text-zinc-400">h</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">worked today</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.keys(PUNCH_LABELS).map((k) => (
                <div key={k} className="px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-sm min-w-[140px]">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{PUNCH_LABELS[k].label}</div>
                  <div className="font-mono font-bold text-zinc-900" data-testid={`today-${k}`}>{formatTime(today[k])}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {Object.keys(PUNCH_LABELS).map((k) => {
              const meta = PUNCH_LABELS[k];
              const done = !!today[k];
              const isNext = nextAction === k;
              return (
                <Button
                  key={k}
                  onClick={() => punch(k)}
                  disabled={done}
                  data-testid={`punch-${k}-btn`}
                  className={`rounded-sm font-semibold text-white ${
                    done ? "bg-zinc-300 text-zinc-500 cursor-not-allowed" : isNext ? meta.color + " ring-2 ring-offset-2 ring-zinc-950" : meta.color
                  }`}
                >
                  <meta.icon className="w-4 h-4 mr-2" />
                  {done ? `${meta.label}: ${formatTime(today[k])}` : meta.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="flex items-end justify-between border-b border-zinc-200 pb-3">
          <div>
            <h2 className="font-heading text-2xl tracking-tight font-bold text-zinc-950">My History</h2>
            <div className="text-sm text-zinc-500 mt-1">
              Pay week: Thursday → Wednesday. {ownHistory.length} entries · {totalOwnHours.toFixed(2)}h total.
            </div>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
            {myPayrollWeeks.length} {myPayrollWeeks.length === 1 ? "week" : "weeks"}
          </div>
        </div>

        {myPayrollWeeks.length === 0 && (
          <div className="mt-4 bg-white border border-zinc-200 rounded-sm px-4 py-8 text-center text-sm text-zinc-500">
            No timecards yet.
          </div>
        )}

        <div className="mt-4 grid gap-6">
          {myPayrollWeeks.map((w) => (
            <div
              key={w.weekStart}
              className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
              data-testid={`my-week-${w.weekStart}`}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-[#002FA7] text-white">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-blue-100">
                    Pay Week · Thu {w.weekStart}
                  </div>
                  <div className="font-mono font-bold text-white text-base mt-0.5">
                    {formatRange(w.weekStart)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-blue-100">Week Total</div>
                  <div
                    className="font-mono font-black text-white text-xl"
                    data-testid={`my-week-total-${w.weekStart}`}
                  >
                    {w.total.toFixed(2)}h
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      {["Date", "Clock In", "Lunch Out", "Lunch In", "Clock Out", "Hours", ""].map((h) => (
                        <th key={h} className="text-left px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {w.entries.map((t) => (
                      <tr key={t.id} className="hover:bg-zinc-50" data-testid={`tc-row-${t.date}`}>
                        <td className="px-4 py-3 text-sm font-semibold text-zinc-950 font-mono">{t.date}</td>
                        <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_in)}</td>
                        <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_out)}</td>
                        <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_in)}</td>
                        <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_out)}</td>
                        <td className="px-4 py-3 text-sm font-mono font-bold text-zinc-950">{t.total_hours.toFixed(2)}h</td>
                        <td className="px-4 py-3 text-right">
                          {isAdmin && (
                            <button onClick={() => openEdit(t)} data-testid={`edit-tc-${t.date}`} className="text-zinc-500 hover:text-zinc-950">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {teamHistory.length > 0 && (
        <div className="mt-12" data-testid="team-timecards-section">
          <div className="flex items-end justify-between border-b border-zinc-200 pb-3">
            <div>
              <h2 className="font-heading text-2xl tracking-tight font-bold text-zinc-950">Team Timecards</h2>
              <div className="text-sm text-zinc-500 mt-1">
                Employees you&apos;ve been granted visibility to. Pay week: Thursday → Wednesday.
              </div>
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
              View only
            </div>
          </div>

          <div className="mt-4 grid gap-6">
            {teamPayrollWeeks.map((w) => (
              <div
                key={w.weekStart}
                className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
                data-testid={`team-week-${w.weekStart}`}
              >
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-700 text-white">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-300">
                      Pay Week · Thu {w.weekStart}
                    </div>
                    <div className="font-mono font-bold text-white text-base mt-0.5">
                      {formatRange(w.weekStart)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-300">Week Total</div>
                    <div
                      className="font-mono font-black text-white text-xl"
                      data-testid={`team-week-total-${w.weekStart}`}
                    >
                      {w.total.toFixed(2)}h
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-zinc-200">
                  {w.rows.map((r) => (
                    <div key={`${w.weekStart}-${r.name}-${r.phone}`}>
                      <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                        <div>
                          <div className="text-sm font-bold text-zinc-950">{r.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{r.phone || "—"}</div>
                        </div>
                        <div className="text-right flex items-center gap-6">
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Days</div>
                            <div className="font-mono font-bold text-zinc-900 text-sm">{r.entries.length}</div>
                          </div>
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Hours</div>
                            <div
                              className="font-mono font-black text-zinc-950 text-base"
                              data-testid={`team-employee-total-${w.weekStart}-${r.name}`}
                            >
                              {r.hours.toFixed(2)}h
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                          <thead className="bg-white border-b border-zinc-200">
                            <tr>
                              {["Date", "Clock In", "Lunch Out", "Lunch In", "Clock Out", "Hours"].map((h) => (
                                <th key={h} className="text-left px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-100">
                            {r.entries.map((t) => (
                              <tr key={t.id} className="hover:bg-zinc-50">
                                <td className="px-4 py-3 text-sm font-mono font-semibold text-zinc-950">{t.date}</td>
                                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_in)}</td>
                                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_out)}</td>
                                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_in)}</td>
                                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_out)}</td>
                                <td className="px-4 py-3 text-sm font-mono font-bold text-zinc-950">{t.total_hours.toFixed(2)}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              Edit {editing?.date}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4" data-testid="tc-edit-form">
              {["clock_in", "lunch_out", "lunch_in", "clock_out"].map((k) => (
                <div key={k}>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                    {PUNCH_LABELS[k].label}
                  </Label>
                  <Input
                    type="datetime-local"
                    value={editForm[k] || ""}
                    onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                    data-testid={`tc-edit-${k}`}
                    className="rounded-sm border-zinc-300 font-mono"
                  />
                </div>
              ))}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button type="submit" data-testid="tc-edit-save" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
